import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Holder {
  customerName: string;
  customerPhone?: string;
  cratesOut: number;
  cratesBack: number;
  holding: number;
  depositHeld: string;
  lastMoved: string;
}

interface Movement {
  id: string;
  customerName: string;
  direction: 'out' | 'back';
  quantity: number;
  note?: string;
  staffName?: string;
  createdAt: string;
}

/**
 * Who is holding your crates.
 *
 * The deposit still goes through the till as the Crate Deposit product, so the
 * money shown here is what you owe back, not a second cash figure.
 */
export default function CratesPanel({ branchId, branchName }: { branchId: string; branchName?: string }) {
  const [holders, setHolders] = useState<Holder[]>([]);
  const [summary, setSummary] = useState({ customers: 0, cratesOut: 0, depositHeld: '0' });
  const [history, setHistory] = useState<Movement[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 4000);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const load = async () => {
    if (!branchId) return;
    try {
      const [b, s, h] = await Promise.all([
        apiClient.crateBalances(branchId),
        apiClient.crateSummary(branchId),
        apiClient.crateHistory(branchId),
      ]);
      setHolders(Array.isArray(b.data) ? b.data : []);
      setSummary(s.data);
      setHistory(Array.isArray(h.data) ? h.data : []);
    } catch (e: any) {
      fail(e, 'Could not load crates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const move = async (direction: 'out' | 'back', customerName?: string, quantity?: number) => {
    const who = (customerName ?? name).trim();
    const howMany = quantity ?? Number(qty);
    if (!who || !howMany) {
      setErr('A crate movement needs a name and a number.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.recordCrateMovement(branchId, {
        customerName: who,
        customerPhone: customerName ? undefined : phone.trim() || undefined,
        direction,
        quantity: howMany,
        note: customerName ? undefined : note.trim() || undefined,
      });
      flash(res.data.message);
      if (!customerName) {
        setName('');
        setPhone('');
        setQty('');
        setNote('');
      }
      await load();
    } catch (e: any) {
      fail(e, 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const field =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Crates out</p>
            <p className="text-3xl font-bold text-amber-900 tabular-nums mt-1">{summary.cratesOut}</p>
            <p className="text-sm text-amber-800 mt-1">
              with {summary.customers} customer{summary.customers === 1 ? '' : 's'}
              {branchName ? ` at ${branchName}` : ''}
            </p>
            <div className="mt-4 pt-3 border-t border-amber-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Deposits you owe back</p>
              <p className="text-2xl font-bold text-amber-900 tabular-nums">
                R {Number(summary.depositHeld).toFixed(2)}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900">Move crates</h3>
            <p className="text-sm text-gray-500 mt-1">Going out with a customer, or coming back.</p>
            <div className="space-y-3 mt-5">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" list="crate-holders" className={field} />
              <datalist id="crate-holders">
                {holders.map((h) => (
                  <option key={h.customerName} value={h.customerName} />
                ))}
              </datalist>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" inputMode="tel" className={field} />
              <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="How many crates" inputMode="numeric" className={`${field} tabular-nums`} />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={field} />
              <div className="flex gap-3">
                <button
                  onClick={() => move('out')}
                  disabled={busy}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
                >
                  Crates out
                </button>
                <button
                  onClick={() => move('back')}
                  disabled={busy}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
                >
                  Crates back
                </button>
              </div>
              <p className="text-xs text-gray-500">
                The R50 deposit is rung up on the till as usual. This only tracks the crates.
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-900">Holding your crates</h3>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-sm text-gray-600 hover:text-gray-900 hover:underline shrink-0"
              >
                {showHistory ? 'Hide movements' : 'Show movements'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {holders.length === 0 ? (
                    <tr>
                      <td className="py-12 text-center text-gray-500">
                        Every crate is back. Nobody is holding any.
                      </td>
                    </tr>
                  ) : (
                    holders.map((h) => (
                      <tr key={h.customerName} className="border-b last:border-0">
                        <td className="py-3 px-6">
                          <p className="font-medium text-gray-900">{h.customerName}</p>
                          <p className="text-xs text-gray-500">
                            {h.customerPhone ? `${h.customerPhone} · ` : ''}
                            {h.cratesOut} out, {h.cratesBack} back
                          </p>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <p className="tabular-nums font-bold text-lg text-gray-900">{h.holding}</p>
                          <p className="text-xs text-gray-500 tabular-nums">R {h.depositHeld}</p>
                        </td>
                        <td className="py-3 px-6 text-right w-40">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => move('back', h.customerName, 1)}
                              disabled={busy}
                              className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded font-medium"
                            >
                              1 back
                            </button>
                            <button
                              onClick={() => move('back', h.customerName, h.holding)}
                              disabled={busy}
                              className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2.5 py-1.5 rounded font-medium"
                            >
                              All {h.holding}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {showHistory && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">Recent movements</h3>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td className="py-10 text-center text-gray-500">Nothing recorded yet.</td>
                      </tr>
                    ) : (
                      history.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-2.5 px-6">
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                m.direction === 'out'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {m.direction === 'out' ? 'out' : 'back'}
                            </span>
                          </td>
                          <td className="py-2.5 px-2 tabular-nums font-semibold text-gray-900">{m.quantity}</td>
                          <td className="py-2.5 px-4">
                            <p className="text-gray-900">{m.customerName}</p>
                            {m.note && <p className="text-xs text-gray-500">{m.note}</p>}
                          </td>
                          <td className="py-2.5 px-6 text-right text-xs text-gray-500">
                            {new Date(m.createdAt).toLocaleString('en-ZA', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                            <span className="block">{m.staffName}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
